'use client';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { Loading } from '@/app/components/icons/Loading';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { CharImgCropModal } from '@/app/components/shared/ImgCropModals/CharImgCropModal';
import { ReferenceCard } from '@/app/components/shared/ReferenceCard';
import { VideoPlayer } from '@/app/components/shared/VideoPlayer';
import { HeadlessVideoUploader } from '@/app/components/shared/VideoUploader/HeadlessVideoUploader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import {
  MODEL_VERSION,
  Q2_PRO_REFERENCE_VIDEO_MAX_DURATION,
  Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE,
  SITE,
} from '@/app/constants';
import {
  useBatchCrop,
  useCheckEnableCreateElement,
  useDevice,
  useDragAndDrop,
  useReferenceVideoDurations,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type {
  ElementModality,
  ElementType,
  ImagePrompt,
  ImageUploadFile,
  ModelVersion,
  ReferencePrompt,
  UploadImageType,
  VideoPrompt,
  VideoUploadFile,
} from '@/app/types';
import { cn, convertViduV1ElementToElementType, getReferenceMediaDropArgs, isReferenceVideoPrompt } from '@/app/utils';
import { getVideoMetadata, resizeSourceImage, uploadFile } from '@/app/utils/client';
import type { ViduV1Element } from '../types';
import {
  ReferencePromptAddCard,
  ReferencePromptElementCard,
  ReferencePromptForbiddenCard,
  ReferencePromptImageCard,
  ReferencePromptUploadingCard,
} from './ReferencePromptCard';

const VIDEO_UPLOADER_BG_IMAGE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/video-a09ab611.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/video-a09ab611.webp';
const IMG_UPLOADER_BG_IMAGE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/img-052445cb.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/img-052445cb.webp';

interface RenderImageCardParams {
  id: string;
  prompt: ImagePrompt | null;
  name: string;
  idx: number;
  isUploading?: boolean;
}

interface RenderElementCardParams {
  id: string;
  prompt: ElementType | ViduV1Element | null;
  idx: number;
  name: string;
  isUploading?: boolean;
  isVideoRow?: boolean;
}

interface VideoImageReferencesGridProps {
  referencePrompts: ReferencePrompt[];
  maxImageSize: number;
  onUpdateReference: (id: string, updates: Partial<ReferencePrompt>) => void;
  onRemoveReference: (id: string) => void;
  onUploadImage: (file: ImageUploadFile, onUploadStatusChange?: (status: ImageUploadFile['status']) => void) => void;
  onUploadVideo: (file: VideoUploadFile) => void;
  onToggleElementsSelector: (allowedModalities?: Array<ElementModality>) => void;
  onOpenElementCreateDialog: (initialImagePrompt: ImagePrompt, imageId: string) => void;
  onOpenElementViewDialog: (element: ElementType, editMode?: boolean) => void;
  replaceImage: (index: number, file: ImageUploadFile) => UploadImageType | null;
  handleSwapReferences: (sourceIdx: number, targetIdx: number) => void;
  modelVersion?: ModelVersion;
  maxVideoDuration?: number;
  minVideoDuration?: number;
  maxVideoSize?: number;
  maxImageAndElementCount?: number;
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
}

export const VideoImageReferencesGrid = forwardRef<HTMLDivElement, VideoImageReferencesGridProps>(
  (
    {
      referencePrompts,
      maxImageSize,
      onUpdateReference,
      onRemoveReference,
      onUploadImage,
      onUploadVideo,
      onToggleElementsSelector,
      onOpenElementCreateDialog,
      onOpenElementViewDialog,
      replaceImage,
      handleSwapReferences,
      modelVersion = MODEL_VERSION.Q2_PRO,
      maxVideoDuration = Q2_PRO_REFERENCE_VIDEO_MAX_DURATION,
      minVideoDuration = 1,
      maxVideoSize = Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE,
      maxImageAndElementCount,
      allowTempMode = false,
    },
    ref,
  ) => {
    const t = useTranslations();
    const { isMobile } = useDevice();
    const [preparingCropIndex, setPreparingCropIndex] = useState<number | null>(null);
    const [uploadDropDownOpen, setUploadDropDownOpen] = useState(false);
    const [videoUploadDropDownOpen, setVideoUploadDropDownOpen] = useState(false);
    const userInfo = useStore((state) => state.userInfo);
    const flyEndId = useStore((state) => state.flyEndId);

    const { checkCreateElementEnablePromise } = useCheckEnableCreateElement();

    // 分离视频和图片/主体
    const videoPrompts = referencePrompts.filter(isReferenceVideoPrompt);
    const imageAndElementPrompts = referencePrompts.filter((item) => !isReferenceVideoPrompt(item));

    // 处理视频元数据和封面图提取
    useEffect(() => {
      const processPromises = videoPrompts
        .filter((item) => item.type === 'video')
        .map(async (videoRef) => {
          const prompt = videoRef.prompt as VideoPrompt | null;
          if (!prompt || !prompt.videoPreview) {
            return;
          }

          const updates: Partial<VideoPrompt> = {};
          const needsDuration = prompt.duration === undefined && !!prompt.video;
          const needsCoverImage = videoRef.status === 'done' && !prompt.coverImage;

          // 如果同时需要 duration 和封面图，一次性获取
          if (needsDuration || needsCoverImage) {
            try {
              const metadata = await getVideoMetadata(prompt.videoPreview, {
                includeDuration: needsDuration,
                captureCoverImage: needsCoverImage,
                needCoverBlob: needsCoverImage, // 需要上传封面图，所以需要 blob
              });

              if (needsDuration && metadata.duration && metadata.duration > 0) {
                updates.duration = metadata.duration;
              }

              if (needsCoverImage && metadata.cover_url) {
                const coverUrl = metadata.cover_url;
                updates.coverImagePreview = coverUrl;

                // 异步上传封面图并更新 coverImage
                (async () => {
                  try {
                    // 直接使用 cover_blob 进行上传，避免 base64 -> fetch -> blob 的冗余转换
                    const coverBlob = metadata.cover_blob;
                    if (!coverBlob) {
                      console.warn('Cover blob not available, skipping upload');
                      return;
                    }

                    const uploadResult = await uploadFile({
                      file: coverBlob,
                      width: metadata.width,
                      height: metadata.height,
                    });

                    if (uploadResult.uri) {
                      onUpdateReference(videoRef.id, {
                        prompt: {
                          ...(videoRef.prompt as VideoPrompt),
                          coverImage: uploadResult.uri,
                        },
                      });
                    }
                  } catch (uploadError) {
                    console.error('Failed to upload video cover image:', uploadError);
                  }
                })();
              }
            } catch (error) {
              // 获取失败时静默处理，不影响用户体验
              console.warn('Failed to get video metadata:', error);
            }
          }

          // 如果有更新，合并更新
          if (Object.keys(updates).length > 0) {
            onUpdateReference(videoRef.id, {
              prompt: {
                ...prompt,
                ...updates,
              },
            });
          }
        });

      // 不等待所有处理完成，让它们异步执行
      void Promise.all(processPromises);
    }, [videoPrompts]);

    // 视频主体的时长上面那段拿不到（它只处理 type === 'video'），交给共享 hook 探测，
    // 这样两个页面的额度判断都基于真实时长。
    useReferenceVideoDurations(referencePrompts);

    const mediaDropArgs = getReferenceMediaDropArgs(modelVersion, referencePrompts, {
      minVideoDuration,
      maxVideoDuration,
    });
    const maxVideoCount = videoPrompts.length + mediaDropArgs.maxVideoCount;
    const imageAndElementMaxCount = imageAndElementPrompts.length + mediaDropArgs.maxImageCount;
    const maxVideoSizeForUpload = mediaDropArgs.maxVideoSize ?? maxVideoSize;

    /**
     * 将子数组索引映射到 referencePrompts 中的实际索引
     * @param subArrayIndex 子数组中的索引
     * @param subArray 子数组（videoPrompts 或 imageAndElementPrompts）
     * @returns referencePrompts 中的实际索引，如果找不到则返回 -1
     */
    const mapSubArrayIndexToReferenceIndex = (subArrayIndex: number, subArray: ReferencePrompt[]): number => {
      if (subArrayIndex < 0 || subArrayIndex >= subArray.length) {
        return -1;
      }
      const item = subArray[subArrayIndex];
      return referencePrompts.findIndex((ref) => ref.id === item.id);
    };

    /**
     * 通用的拖拽重排序处理函数
     * @param sourceIdx 源索引（子数组中的索引）
     * @param targetIdx 目标索引（子数组中的索引）
     * @param subArray 子数组（videoPrompts 或 imageAndElementPrompts）
     */
    const handleReorder = (sourceIdx: number, targetIdx: number, subArray: ReferencePrompt[]) => {
      const sourceRefIndex = mapSubArrayIndexToReferenceIndex(sourceIdx, subArray);
      const targetRefIndex = mapSubArrayIndexToReferenceIndex(targetIdx, subArray);
      if (sourceRefIndex !== -1 && targetRefIndex !== -1) {
        handleSwapReferences(sourceRefIndex, targetRefIndex);
      }
    };

    // 拖动换位逻辑（用于视频）
    const {
      onDragStart: onVideoDragStart,
      onDragOver: onVideoDragOver,
      onDropCapture: onVideoDropCapture,
    } = useDragAndDrop({
      onReorder: (sourceIdx, targetIdx) => handleReorder(sourceIdx, targetIdx, videoPrompts),
    });

    // 拖动换位逻辑（用于图片和主体）
    const {
      onDragStart: onImageDragStart,
      onDragOver: onImageDragOver,
      onDropCapture: onImageDropCapture,
    } = useDragAndDrop({
      onReorder: (sourceIdx, targetIdx) => handleReorder(sourceIdx, targetIdx, imageAndElementPrompts),
    });

    const updateImagePromptByIds = (imagePromptMap: { id: string; prompt: ImagePrompt }[]) => {
      imagePromptMap.forEach(({ id, prompt }) => {
        onUpdateReference(id, {
          prompt,
          reference: undefined, // 裁剪后图片已变化，清除 creation_id 引用
        });
      });
    };

    // 用于对图片选择主体范围
    const { selectModalImageSource, handleCloseSelectModal, handleOpenSelectModal, handleSaveChangedImagePrompts } =
      useBatchCrop({ maxImageSize, updateImagePromptByIds, allowTempMode });

    const handleClickCropButton = async (idx: number, prompt?: ImagePrompt | null) => {
      try {
        setPreparingCropIndex(idx);
        await handleOpenSelectModal(idx, prompt);
      } finally {
        setPreparingCropIndex(null);
      }
    };

    const renderRemoveButton = (id: string) => {
      return (
        <div
          className="flex size-6 cursor-pointer items-center justify-center rounded-full bg-system-white24 text-system-white backdrop-blur-[2px]"
          onClick={() => onRemoveReference(id)}
        >
          <BaseLineClose className="text-base" />
        </div>
      );
    };

    const renderVideoCard = (
      id: string,
      prompt: VideoPrompt | null,
      name: string,
      idx: number,
      isUploading?: boolean,
    ) => {
      return (
        <ReferenceCard
          id={id}
          ref={id === flyEndId ? ref : undefined}
          draggable={true}
          onDragStart={(e) => onVideoDragStart(e, idx)}
          onDragOver={(e) => onVideoDragOver(e)}
          onDropCapture={(e) => onVideoDropCapture(e, idx)}
          footer={name}
          deleteButton={isUploading ? undefined : renderRemoveButton(id)}
          expandedFooter={
            prompt
              ? {
                  left: name,
                }
              : undefined
          }
        >
          {prompt?.videoPreview ? (
            <VideoPlayer
              src={prompt.videoPreview}
              poster={prompt.coverImagePreview || prompt.coverImage}
              autoPlay={false}
              creationId=""
              className="size-full object-cover"
              playOnHover={true}
              hideControls={true}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Loading className="animate-spin text-2xl" />
            </div>
          )}
        </ReferenceCard>
      );
    };

    const renderImageCard = ({ id, prompt, name, idx, isUploading }: RenderImageCardParams) => {
      const isCropLoading = preparingCropIndex === idx;
      const actualIdx = referencePrompts.findIndex((item) => item.id === id);

      return (
        <ReferencePromptImageCard
          id={id}
          ref={id === flyEndId ? ref : undefined}
          prompt={prompt}
          name={name}
          indexLabel={idx + 1}
          onDragStart={(e) => onImageDragStart(e, idx)}
          onDragOver={(e) => onImageDragOver(e)}
          onDropCapture={(e) => onImageDropCapture(e, idx)}
          deleteButton={isUploading ? undefined : renderRemoveButton(id)}
          maxImageSize={maxImageSize}
          replaceImage={(file) => replaceImage(actualIdx, file)}
          allowTempMode={allowTempMode}
          isCropLoading={isCropLoading}
          onCrop={() => handleClickCropButton(idx, prompt)}
          onRemove={() => onRemoveReference(id)}
          onCreateElement={() => {
            if (!prompt) return;
            checkCreateElementEnablePromise({ query: {} }).then(({ error }) => {
              if (error) return;
              onOpenElementCreateDialog(prompt, id);
            });
          }}
          contentClassName={prompt && id === flyEndId ? 'animate-fade-in opacity-0' : undefined}
        />
      );
    };

    const renderElementCard = ({ id, prompt, idx, name, isUploading, isVideoRow }: RenderElementCardParams) => {
      if (!prompt) {
        return null;
      }

      // 如果是 ViduV1Element 类型，则转换为 ElementType
      const element = !('description' in prompt) ? convertViduV1ElementToElementType(prompt) : prompt;

      // 根据元素类型和所在行决定使用哪个拖拽处理函数
      const isVideoElement = prompt.modality === 'video' || isVideoRow;
      const dragHandlers = isVideoElement
        ? {
            onDragStart: (e: React.DragEvent) => onVideoDragStart(e, idx),
            onDragOver: onVideoDragOver,
            onDropCapture: (e: React.DragEvent) => onVideoDropCapture(e, idx),
          }
        : {
            onDragStart: (e: React.DragEvent) => onImageDragStart(e, idx),
            onDragOver: onImageDragOver,
            onDropCapture: (e: React.DragEvent) => onImageDropCapture(e, idx),
          };

      const isVideoModality = prompt.modality === 'video';

      return (
        <ReferencePromptElementCard
          id={id}
          element={element}
          name={name}
          onDragStart={dragHandlers.onDragStart}
          onDragOver={dragHandlers.onDragOver}
          onDropCapture={dragHandlers.onDropCapture}
          deleteButton={isUploading || isMobile ? undefined : renderRemoveButton(id)}
          showVideoIcon={isVideoModality}
          showActions={!isMobile}
          showTopRightSlot={!isMobile}
          canEdit={
            prompt?.type === 'user' &&
            prompt.creator_id === userInfo?.id &&
            !['approved', 'pending'].includes(prompt?.share_meta?.status || '')
          }
          onView={() => onOpenElementViewDialog(element)}
          onEdit={() => onOpenElementViewDialog(element, true)}
        />
      );
    };

    const renderForbiddenCard = (id: string, name: string) => {
      return <ReferencePromptForbiddenCard key={id} id={id} name={name} deleteButton={renderRemoveButton(id)} />;
    };

    /**
     * 渲染添加媒体或元素的卡片
     */
    const renderAddMediaOrElementCard = (firstButton: React.ReactNode, onAddElements: () => void) => {
      return (
        <ReferencePromptAddCard
          imageSlot={firstButton}
          elementSlot={
            <div
              className="flex w-full cursor-pointer items-center justify-center gap-1 transition-colors hover:bg-system-white08"
              onClick={onAddElements}
            >
              <BaseLinePlus className="text-base text-system-white" />
              <span className="whitespace-nowrap text-system-white64 text-xs">
                {t('create.char2VideoForm.element.addElements')}
              </span>
            </div>
          }
        />
      );
    };

    /**
     * 渲染视频上传按钮
     */
    const renderVideoUploadButton = ({
      buttonClassName,
      iconClassName,
      textClassName,
      onChange,
    }: {
      buttonClassName?: string;
      iconClassName?: string;
      textClassName?: string;
      onChange?: (file: VideoUploadFile) => void;
    }) => {
      return (
        <HeadlessVideoUploader
          maxDuration={mediaDropArgs.maxVideoDuration}
          minDuration={mediaDropArgs.minVideoDuration}
          maxSize={maxVideoSizeForUpload}
          captureCoverImage={true}
          uploadCoverImage={true}
          allowTempMode={allowTempMode}
          onChange={onChange || onUploadVideo}
        >
          {({ openFileSelector }) => (
            <div
              className={cn(
                'flex cursor-pointer items-center justify-center gap-1 transition-colors hover:bg-system-white08',
                buttonClassName,
              )}
              onClick={openFileSelector}
            >
              <BaseLinePlus className={cn('text-system-white', iconClassName)} />
              <span className={textClassName}>{t('create.createForm.upload.video')}</span>
            </div>
          )}
        </HeadlessVideoUploader>
      );
    };

    /**
     * 渲染媒体上传下拉菜单
     */
    const renderMediaUploadDropdown = ({
      open,
      onOpenChange,
      triggerTitle,
      triggerSubtitle,
      triggerClassName,
      backgroundImage,
      firstMenuItem,
      onAddElements,
    }: {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      triggerTitle: string;
      triggerSubtitle: string;
      triggerClassName: string;
      backgroundImage: string;
      firstMenuItem: React.ReactNode;
      onAddElements: () => void;
    }) => {
      return (
        <DropdownMenu open={open} onOpenChange={onOpenChange}>
          <DropdownMenuTrigger asChild>
            <div
              className={cn(
                'relative flex h-24 w-full cursor-pointer items-center justify-start gap-3 pl-5 transition-colors hover:bg-system-white08',
                triggerClassName,
              )}
            >
              <div className="flex size-8 items-center justify-center rounded-full bg-system-white08">
                <BaseLinePlus className="text-system-white text-xl" />
              </div>
              <div className="relative z-10 flex flex-col">
                <span className="mb-1 font-semibold text-sm text-system-white leading-tight">{triggerTitle}</span>
                <span className="text-system-white48 text-xs leading-tight">{triggerSubtitle}</span>
              </div>
              <img
                src={backgroundImage}
                alt=""
                className="absolute right-0 bottom-0 aspect-[185/75] w-[185px] md:hidden lg:w-[148px]"
              />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-[100px] gap-0 bg-[#1A1C20]/80 p-0 backdrop-blur-[5px]">
            <DropdownMenuItem onClick={(e) => e.preventDefault()} className="p-0">
              {firstMenuItem}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-0" />
            <DropdownMenuItem
              className="flex items-center justify-center gap-1 px-2 py-3 text-xs"
              onClick={onAddElements}
            >
              <BaseLinePlus className="text-sm" />
              <span>{t('create.char2VideoForm.element.addElements')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    };

    return (
      <>
        <div className="w-full select-none rounded-16 border border-[#35383F] border-dashed bg-system-black36 py-2">
          {/* 第一行：视频上传区域 */}
          {referencePrompts.length > 0 ? (
            <div className="flex min-h-[120px] items-center justify-center gap-2">
              {videoPrompts.map(({ prompt, type, name, id, status }, idx) => {
                const isUploading = status === 'uploading';
                if (type === 'video') {
                  if (isUploading) {
                    return (
                      <div key={id} className="w-full max-w-[100px]">
                        <ReferencePromptUploadingCard id={id} label={t('create.createForm.upload.uploading')} />
                      </div>
                    );
                  }
                  return (
                    <div key={id} className="w-full max-w-[100px]">
                      {renderVideoCard(id, prompt as VideoPrompt, name, idx, isUploading)}
                    </div>
                  );
                }
                if (type === 'element' && (prompt || isUploading) && status !== 'forbidden') {
                  if (isUploading) {
                    return (
                      <div key={id} className="w-full max-w-[100px]">
                        <ReferencePromptUploadingCard id={id} label={t('create.createForm.upload.uploading')} />
                      </div>
                    );
                  }
                  return (
                    <div key={id} className="w-full max-w-[100px]">
                      {renderElementCard({
                        id,
                        prompt: isUploading ? null : prompt,
                        idx,
                        name,
                        isUploading,
                        isVideoRow: true,
                      })}
                    </div>
                  );
                }
                if (status === 'forbidden') {
                  return (
                    <div key={id} className="w-full max-w-[100px]">
                      {renderForbiddenCard(id, name)}
                    </div>
                  );
                }
                return null;
              })}
              {maxVideoCount > videoPrompts.length && (
                <div className="w-full max-w-[100px]">
                  {renderAddMediaOrElementCard(
                    renderVideoUploadButton({
                      buttonClassName: 'w-full',
                      iconClassName: 'text-base',
                      textClassName: 'text-system-white64 text-xs',
                    }),
                    () => onToggleElementsSelector(['video']),
                  )}
                </div>
              )}
            </div>
          ) : (
            renderMediaUploadDropdown({
              open: videoUploadDropDownOpen,
              onOpenChange: setVideoUploadDropDownOpen,
              triggerTitle: t('create.createForm.upload.uploadClips'),
              triggerSubtitle: t('create.createForm.upload.videoDurationLimit'),
              triggerClassName: 'rounded-t-16',
              backgroundImage: VIDEO_UPLOADER_BG_IMAGE,
              firstMenuItem: renderVideoUploadButton({
                buttonClassName: 'w-full px-2 py-3 text-xs',
                iconClassName: 'text-sm',
                onChange: (file) => {
                  onUploadVideo(file);
                  if (file.status === 'done') {
                    setVideoUploadDropDownOpen(false);
                  }
                },
              }),
              onAddElements: () => {
                onToggleElementsSelector(['video']);
                setVideoUploadDropDownOpen(false);
              },
            })
          )}

          <div className="mx-5 my-1 h-[1px] w-calc(100%-40px) bg-system-white08" />

          {/* 第二行：图片和主体上传区域 */}
          {referencePrompts.length > 0 ? (
            <div className="flex min-h-[120px] items-center justify-center gap-2">
              {imageAndElementPrompts.map(({ prompt, status, type, name, id }, idx) => {
                const isUploading = status === 'uploading';
                if (type === 'image' && (prompt || isUploading)) {
                  if (isUploading) {
                    return (
                      <div key={id} className="w-full max-w-[100px]">
                        <ReferencePromptUploadingCard id={id} label={t('create.createForm.upload.uploading')} />
                      </div>
                    );
                  }
                  return (
                    <div key={id} className="w-full max-w-[100px]">
                      {renderImageCard({
                        id,
                        prompt: isUploading ? null : (prompt as ImagePrompt),
                        name,
                        idx,
                        isUploading,
                      })}
                    </div>
                  );
                }
                if (type === 'element' && (prompt || isUploading) && status !== 'forbidden') {
                  if (isUploading) {
                    return (
                      <div key={id} className="w-full max-w-[100px]">
                        <ReferencePromptUploadingCard id={id} label={t('create.createForm.upload.uploading')} />
                      </div>
                    );
                  }
                  return (
                    <div key={id} className="w-full max-w-[100px]">
                      {renderElementCard({
                        id,
                        prompt: isUploading ? null : prompt,
                        idx,
                        name,
                        isUploading,
                        isVideoRow: false,
                      })}
                    </div>
                  );
                }
                if (status === 'forbidden') {
                  return (
                    <div key={id} className="w-full max-w-[100px]">
                      {renderForbiddenCard(id, name)}
                    </div>
                  );
                }
                return null;
              })}

              {imageAndElementPrompts.length < imageAndElementMaxCount && (
                <div className="w-full max-w-[100px]">
                  {renderAddMediaOrElementCard(
                    <ImageUploader
                      className="flex flex-1 cursor-pointer items-center justify-center gap-1 transition-colors hover:bg-system-white08"
                      maxCount={mediaDropArgs.maxImageCount}
                      getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                      onChange={(file) => {
                        onUploadImage(file);
                      }}
                      allowTempMode={allowTempMode}
                    >
                      <BaseLinePlus className="text-base text-system-white" />
                      <span className="text-system-white64 text-xs">
                        {t('create.char2VideoForm.element.addImages')}
                      </span>
                    </ImageUploader>,
                    () => onToggleElementsSelector(['image', 'text']),
                  )}
                </div>
              )}
            </div>
          ) : (
            renderMediaUploadDropdown({
              open: uploadDropDownOpen,
              onOpenChange: setUploadDropDownOpen,
              triggerTitle: t('create.createForm.upload.uploadImagesRefs'),
              triggerSubtitle: t('create.createForm.upload.imageCountLimit'),
              triggerClassName: 'rounded-b-16',
              backgroundImage: IMG_UPLOADER_BG_IMAGE,
              firstMenuItem: (
                <ImageUploader
                  className="flex w-full items-center justify-center gap-1 px-2 py-3 text-xs"
                  maxCount={mediaDropArgs.maxImageCount}
                  getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                  onChange={(file) => {
                    onUploadImage(file, (status) => {
                      if (status === 'done') {
                        setUploadDropDownOpen(false);
                      }
                    });
                  }}
                  allowTempMode={allowTempMode}
                >
                  <BaseLinePlus className="text-sm" />
                  <span>{t('create.char2VideoForm.element.addImages')}</span>
                </ImageUploader>
              ),
              onAddElements: () => {
                onToggleElementsSelector(['image', 'text']);
                setUploadDropDownOpen(false);
              },
            })
          )}
        </div>

        <CharImgCropModal
          imageSource={selectModalImageSource}
          referencePrompts={imageAndElementPrompts}
          onCancel={handleCloseSelectModal}
          onConfirm={handleSaveChangedImagePrompts}
          handleClickImagePrompt={handleOpenSelectModal}
          handleRemoveReference={onRemoveReference}
          maxImageSize={maxImageSize}
          onUploadImage={onUploadImage}
          replaceImage={replaceImage}
          handleExchange={(sourceIdx, targetIdx) => handleReorder(sourceIdx, targetIdx, imageAndElementPrompts)}
          maxImageAndElementCount={maxImageAndElementCount}
        />
      </>
    );
  },
);

VideoImageReferencesGrid.displayName = 'VideoImageReferencesGrid';
